import { useMemo } from 'react';
import { useParams, useRouter, useSearchParams } from '@/lib/navigation';
import { useTranslations } from '@/i18n';
import { useUser } from '@/hooks/auth/useUser';
import { useTrip } from '@/hooks/trips/useTrip';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { Button } from '@/components/ui/button';
import TripFormLayout from '@/components/trips/TripFormLayout';
import { parseISO } from 'date-fns';
import type { TripFormValues } from '@/components/trips/form/TripForm';

export function EditTripPage() {
  const params = useParams();
  const router = useRouter();
  const [searchParams] = useSearchParams();
  const tripId = params.id as string;

  const tTrips = useTranslations('trips');
  const tNav = useTranslations('navigation');
  const { data: user } = useUser();

  const { data: trip, isLoading, isError } = useTrip(tripId);
  const parentTripId = trip?.parentId ?? null;
  const { data: parentTrip } = useTrip(parentTripId ?? '');
  const derivedFromQuery = (() => {
    const value = searchParams.get('derived');
    if (value === 'extend' || value === 'shorten') {
      return value;
    }
    return null;
  })();
  const derivedMode = useMemo<'extend' | 'shorten' | null>(() => {
    if (derivedFromQuery) {
      return derivedFromQuery;
    }
    if (!parentTrip || !parentTripId) {
      return null;
    }

    // Check the PARENT trip's status to determine the mode
    if (parentTrip.status === 'extended') {
      return 'extend';
    }
    if (parentTrip.status === 'shortened') {
      return 'shorten';
    }

    return null;
  }, [derivedFromQuery, parentTrip, parentTripId]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Spinner variant="circle" />
      </div>
    );
  }

  if (isError || !trip) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <p className="mb-4 text-lg text-[#666666]">{tTrips('notFound')}</p>
          <Button onClick={() => router.push('/')}>{tNav('back')}</Button>
        </div>
      </div>
    );
  }

  const parseDate = (value?: string | null) => {
    if (!value) return null;
    const parsed = parseISO(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const childOriginalEndDate = parseDate(trip.endDate);

  const resolvedStartDate =
    derivedMode === 'shorten' && parentTrip?.startDate
      ? parseDate(parentTrip.startDate)
      : parseDate(trip.startDate);
  const resolvedEndDate = childOriginalEndDate;

  const initialData: TripFormValues = {
    tripName: trip.tripName ?? '',
    tripType: (trip.tripType as 'local' | 'international') ?? 'local',
    initiator: String(trip.initiator.id ?? ''),
    actingEmployee: trip.actingEmployee?.id ? String(trip.actingEmployee.id) : '',
    permanentResidenceCountry: trip.permanentResidenceCountryId
      ? String(trip.permanentResidenceCountryId)
      : '',
    permanentResidenceCity: trip.permanentResidenceCityId
      ? String(trip.permanentResidenceCityId)
      : '',
    startDate: resolvedStartDate,
    endDate: resolvedEndDate,
    purpose: trip.purpose ?? '',
    additionalComment: trip.additionalComment ?? '',
    roamingExpense: Boolean(trip.roamingExpense),
    roamingAmount: trip.roamingAmount ?? '',
    roamingPhoneNumber: trip.phoneNumber ?? '',
    accommodationExpense: Boolean(trip.accommodationExpense),
    dailyPayoutAmount: trip.accommodationAmount ?? '',
  };

  return (
    <TripFormLayout
      mode="edit"
      tripId={tripId}
      tripNumber={trip.number}
      initialData={initialData}
      currentUser={user}
      tripAuthor={trip.author}
      backUrl={`/trips/${tripId}`}
      derivedMode={derivedMode}
      parentTrip={parentTrip ?? null}
      parentTripId={parentTripId}
      parentRequestNumber={trip.parentRequestNumber ?? null}
      actingEmployeeName={trip.actingEmployee?.name}
    />
  );
}
